import { FC, useCallback } from 'react';
import { Col, Row } from 'reactstrap';
import { css } from '@emotion/react';
import { useForm, useFormState } from '@/utils/hooks';
import { CompanySettings, SurveyDifferenceApplicableType } from '@/types/company-settings';
import { formatBooleanSelectorValue } from '@/store/company-settings/helpers';
import { FieldsStateType } from '@/common/types/form';

import Category from 'views/companies/settings/Category';
import SubCategory from 'views/companies/settings/SubCategory';
import VisibleSetting from 'views/companies/settings/VisibleSetting';
import InputLabel from 'views/companies/settings/components/InputLabel';
import Select from '@/library/components/form/inputs/select';
import config, {
  apply_the_bunker_survey_difference_as_correction_to,
  apply_the_lubricant_survey_difference_as_correction_to,
  BunkerSurveyDifferenceApplicableOptionType,
  CaptainReportsType
} from './config';
import InfoPanel from '@/common/components/general/InfoPanel';
import { CATEGORY_IDS } from '../constants';
import { useCategoryDraft } from '../useCategoryDraft';
import { formatOptionDisplay } from '../change-tracking';
import { useCompanySettingsDraft } from '../draft-context';

type CaptainReportsProps = {
  companySettings: CompanySettings | undefined;
};

const CATEGORY_ID = CATEGORY_IDS.captain_reports;
const CATEGORY_TITLE = 'Captain Reports';

const options = Object.values(SurveyDifferenceApplicableType).map(value => ({
  label: value.replace(/_/g, ' '),
  value
}));

const LABELS = {
  [apply_the_bunker_survey_difference_as_correction_to]: 'Bunker Survey Difference',
  [apply_the_lubricant_survey_difference_as_correction_to]: 'Lubricants Survey Difference'
};

const CaptainReports: FC<CaptainReportsProps> = ({ companySettings }) => {
  const { formState, loadValues, collectValues } = useForm(config);
  const { selectField, ...rest } = useFormState(formState);
  const fields: FieldsStateType<CaptainReportsType> = rest.fields;
  const { isSettingVisible } = useCompanySettingsDraft();

  const initializeForm = useCallback(() => {
    if (!companySettings) return;

    loadValues({
      [apply_the_bunker_survey_difference_as_correction_to]:
        companySettings.apply_the_bunker_survey_difference_as_correction_to,
      [apply_the_lubricant_survey_difference_as_correction_to]:
        companySettings.apply_the_lubricant_survey_difference_as_correction_to
    });
  }, [companySettings, loadValues]);

  useCategoryDraft({
    categoryId: CATEGORY_ID,
    categoryTitle: CATEGORY_TITLE,
    companySettings,
    initializeForm,
    collectValues: (validate = true) => collectValues(validate) as CaptainReportsType | null,
    toPayload: values => [
      {
        key: apply_the_bunker_survey_difference_as_correction_to,
        value: formatBooleanSelectorValue<SurveyDifferenceApplicableType>(
          values[apply_the_bunker_survey_difference_as_correction_to]
        )
      },
      {
        key: apply_the_lubricant_survey_difference_as_correction_to,
        value: formatBooleanSelectorValue<SurveyDifferenceApplicableType>(
          values[apply_the_lubricant_survey_difference_as_correction_to]
        )
      }
    ],
    labels: LABELS,
    formatValue: (_key, value) => formatOptionDisplay(value, options),
    watch: fields
  });

  return (
    <Category id={CATEGORY_ID} title={CATEGORY_TITLE}>
      <SubCategory
        title="Bunker Survey"
        hidden={
          !isSettingVisible(CATEGORY_ID, apply_the_bunker_survey_difference_as_correction_to) &&
          !isSettingVisible(CATEGORY_ID, apply_the_lubricant_survey_difference_as_correction_to)
        }
        className="cpb-2 cmb-2"
      >
        <div
          css={css`
            .form-field {
              width: 10rem;
            }
          `}
        >
          <Row className="d-flex align-items-start gx-1">
            <Col xs="auto">
              <VisibleSetting
                categoryId={CATEGORY_ID}
                settingKey={apply_the_bunker_survey_difference_as_correction_to}
              >
                <Select<BunkerSurveyDifferenceApplicableOptionType>
                  label={
                    <InputLabel
                      title="BUNKER SURVEY DIFFERENCE"
                      apiKey={apply_the_bunker_survey_difference_as_correction_to}
                    />
                  }
                  options={options}
                  isAsync={false}
                  isMulti={false}
                  onChange={(option: BunkerSurveyDifferenceApplicableOptionType | null) => {
                    selectField(apply_the_bunker_survey_difference_as_correction_to)(option);
                  }}
                  value={
                    fields[apply_the_bunker_survey_difference_as_correction_to]
                      .value as BunkerSurveyDifferenceApplicableOptionType | null
                  }
                  error={fields[apply_the_bunker_survey_difference_as_correction_to].error}
                />
              </VisibleSetting>

              <VisibleSetting
                categoryId={CATEGORY_ID}
                settingKey={apply_the_lubricant_survey_difference_as_correction_to}
              >
                <Select<BunkerSurveyDifferenceApplicableOptionType>
                  className="mb-0"
                  label={
                    <InputLabel
                      title="LUBRICANTS SURVEY DIFFERENCE"
                      apiKey={apply_the_lubricant_survey_difference_as_correction_to}
                    />
                  }
                  options={options}
                  isAsync={false}
                  isMulti={false}
                  onChange={(option: BunkerSurveyDifferenceApplicableOptionType | null) => {
                    selectField(apply_the_lubricant_survey_difference_as_correction_to)(option);
                  }}
                  value={
                    fields[apply_the_lubricant_survey_difference_as_correction_to]
                      .value as BunkerSurveyDifferenceApplicableOptionType | null
                  }
                  error={fields[apply_the_lubricant_survey_difference_as_correction_to].error}
                />
              </VisibleSetting>
            </Col>

            <Col className="ps-4 position-relative bottom-10">
              <InfoPanel>
                <div className="mb-1">
                  When this is enabled, the <strong>Bunker Survey Difference</strong> is applied
                  automatically as a <strong>correction</strong> to either the departure report or
                  the port noon report.
                </div>
                <div className="mb-1">
                  {`• When 'Departure' is selected, enable the related 'Bunker Survey' and
                'Correction' fields in the departure report template. When a new Departure Report is created, the
                Correction field will be pre-filled with the difference from the previous Bunker
                Survey.`}
                </div>
                <div className="mb-1">
                  {`• When 'Port Noon or Departure' is selected, enable the related 'Bunker Survey' and 'Correction'
                fields in the departure report & port noon report templates. 
                The Correction field of the latest departure or port noon report will be pre-filled with the difference from the previous Bunker Survey.`}
                </div>
                <div>{`• When 'None' is selected, the Correction field won't be pre-filled.`}</div>
              </InfoPanel>
            </Col>
          </Row>
        </div>
      </SubCategory>
    </Category>
  );
};

export default CaptainReports;
